import { useCallback, useEffect, useState } from 'preact/hooks';
import { getItem, setItem } from '../lib/storage.js';

const STORAGE_KEY = 'carii-portal-theme';

type Theme = 'light' | 'dark';

function systemTheme(): Theme {
  return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

/**
 * Theme is `null` (follow OS) until the visitor explicitly toggles it, then
 * persists as an override via lib/storage.ts (FR-005). Never touches
 * `window.localStorage` directly — storage.ts hides the private-browsing
 * failure mode so a blocked-storage visitor still gets a working toggle for
 * the current visit (FR-006).
 */
export function useTheme() {
  const [override, setOverride] = useState<Theme | null>(null);

  useEffect(() => {
    const stored = getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      setOverride(stored);
    }
  }, []);

  const effectiveTheme = override ?? systemTheme();

  useEffect(() => {
    if (override) {
      document.body.setAttribute('data-theme', override);
    } else {
      document.body.removeAttribute('data-theme');
    }
  }, [override]);

  const toggleTheme = useCallback(() => {
    const next: Theme = effectiveTheme === 'light' ? 'dark' : 'light';
    setOverride(next);
    setItem(STORAGE_KEY, next);
  }, [effectiveTheme]);

  return { effectiveTheme, toggleTheme };
}
